import { Form } from 'components/form'
import { useAlert } from 'contexts/alert'
import { IConnectorPermissionDTO } from 'data/dtos/automation/i-connector-permission-dto'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useHistory, useParams } from 'react-router-dom'
import api from 'services/api'

import { ajvResolver } from '@hookform/resolvers/ajv'
import ConnectorPermissionSchema, {
  IConnectorPermissionFormData,
} from 'schema/automation/connector-permission'
import { useStyles } from './styles'

interface IRouteParams {
  id: string
}

const ConnectorPermissionForm: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false)
  const [readonly, setReadonly] = useState(false)
  const [conduccoConnectorsA, setConduccoConnectorsA] = useState([])
  const { closeAlert, openAlert } = useAlert()
  const params = useParams<IRouteParams>()
  const firstInputElement = useRef(null)
  const classes = useStyles()
  const history = useHistory()

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
    control,
    setFocus,
  } = useForm<IConnectorPermissionFormData>({
    resolver: ajvResolver(ConnectorPermissionSchema),
    defaultValues: {
      conduccoConnectorId: '',
      read: false,
      write: false,
      edit: false,
      isInactive: false,
    },
  })

  const handleChange = (formField: any) => closeAlert()

  // main data load
  useEffect(() => {
    async function loadData() {
      const { id } = params

      // form data
      await api
        .get(`/connectors-permissions/${id}`)
        .then((response) => {
          const { data } = response.data

          const connectorPermissionResult = {
            conduccoConnectorId: data.conduccoConnectorId.id,
            read: data.read,
            write: data.write,
            edit: data.edit,
            isInactive: data.isInactive,
          }

          return connectorPermissionResult
        })
        .then((connectorPermissionResult: IConnectorPermissionDTO) => {
          reset(connectorPermissionResult)
        })
        .catch((error) => {
          console.log(error)
          return error
        })
    }

    if (params.id) {
      loadData()
      setReadonly(true)
    }
  }, [params, params.id])

  // data save
  const onSubmit = useCallback(async (data: IConnectorPermissionDTO) => {
    setIsLoading(true)

    const payLoad: IConnectorPermissionDTO = {
      conduccoConnectorId: data.conduccoConnectorId,
      read: data.read,
      write: data.write,
      edit: data.edit,
      isInactive: data.isInactive,
    }

    if (params.id) {
      const { id } = params

      payLoad.id = id

      await api
        .put(`/connectors-permissions`, payLoad)
        .then(() => {
          openAlert('Cliente atualizado com sucesso!', 'success')
          history.push('/connectors-permissions')
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    } else {
      await api
        .post('/connectors-permissions', payLoad)
        .then(() => {
          openAlert('Dado cadastrado com sucesso!', 'success')
          reset()
        })
        .catch((error) => {
          openAlert(error?.response?.data?.data?.message ?? 'Ops! Algo inesperado aconteceu!', 'error')
        })
        .finally(() => setIsLoading(false))
    }
  }, [])

  return (
    <Form.Root onHandleSubmit={handleSubmit(onSubmit)}>
      <Form.Info
        title='Permissões a Conectores'
        subTitle='Dados coletados sobre os logs de execução das receitas de automação.'
      />

      <Form.Body>
        <Form.Select
          label='Conector'
          error={!!errors.conduccoConnectorId}
          errorMessage={errors?.conduccoConnectorId?.message}
          disabled={isLoading || readonly}
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='conduccoConnectorId'
          rules={{
            onChange: (e) => {
              setValue('conduccoConnectorId', e.target.value)
              handleChange(e)
            },
          }}
          register={register}
          value={`${watch().conduccoConnectorId}`}
          options={conduccoConnectorsA}
        />

        <Form.CheckBox
          label='Leitura'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='read'
          control={control}
        />

        <Form.CheckBox
          label='Gravação'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='write'
          control={control}
        />

        <Form.CheckBox
          label='Edição'
          sizing={{ xs: 12, sm: 12, md: 4, lg: 4, xl: 4 }}
          name='edit'
          control={control}
        />

        <Form.CheckBox
          label='Inativo'
          sizing={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}
          name='isInactive'
          control={control}
        />
      </Form.Body>

      <Form.Divisor />

      <Form.Footer>
        <Form.Button
          label='SALVAR'
          type='submit'
          variant='contained'
          isLoading={isLoading}
        />
        <Form.Button
          label='CANCELAR'
          type='cancel'
          variant='text'
          isLoading={isLoading}
        />
      </Form.Footer>
    </Form.Root>
  )
}

export default ConnectorPermissionForm
